import { tracked } from "@glimmer/tracking";
import type MedicModel from "parog-web/models/medic";
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";

export default class PinCodeManager {
  @tracked typedPinCode: string | undefined;
  @tracked private _pinCode: string | undefined;

  declare medic: MedicModel;

  _cacheDate: Date | undefined;

  /**
   * How much time do we keep the pin code in cache in milliseconds.
   */
  _cacheDuration: number = 1000 * 60 * 30; // 30 min

  constructor(medic: MedicModel) {
    this.medic = medic;

    const cachedPinCode = getItemFromLocalStorage(this.pinCodeKey(medic));
    this._pinCode = cachedPinCode || undefined;

    const storedCacheDate = getItemFromLocalStorage(this.cacheDateKey());
    this._cacheDate = storedCacheDate ? new Date(storedCacheDate) : undefined;
  }

  get pinCode(): string | undefined {
    // Don't need to reset the _cacheDate as the readCpsCard function will be called just after
    if (!this.isExpired()) return this._pinCode;
    return undefined;
  }

  get isTypedPinCodeValid(): boolean {
    return pinCodeValid(this.typedPinCode);
  }

  get isStoredPinCodeValid(): boolean {
    return pinCodeValid(this.pinCode);
  }

  refreshCacheDate(): void {
    this._cacheDate = new Date();
  }

  /**
   * Cache the given pin code for a specific duration
   */
  cachePinCode(pinCode: string): void {
    this._pinCode = pinCode;
    setItemInLocalStorage(this.pinCodeKey(this.medic), pinCode);
    this._cacheDate = new Date();
    setItemInLocalStorage(this.cacheDateKey(), this._cacheDate.toISOString());
  }

  resetPinCode(): void {
    this._pinCode = undefined;
    this.typedPinCode = undefined;
    removeItemFromLocalStorage(this.pinCodeKey(this.medic));
    this._cacheDate = undefined;
  }

  private isExpired(): boolean {
    if (!this._pinCode) return false;
    if (!this._cacheDate) return false;
    return (
      new Date().getTime() - this._cacheDate.getTime() >= this._cacheDuration
    );
  }

  private cacheDateKey(): string {
    return "pin-code-manager:cache-date";
  }

  private pinCodeKey(medic): string {
    return `pin-code-manager:medic-${medic.id}:pin-code`;
  }
}

function pinCodeValid(pinCode) {
  const NUMBER_OF_DIGITS = 4;
  return pinCode?.match(/^\d+$/) != null && pinCode.length === NUMBER_OF_DIGITS;
}
